import { useState } from "react";
import { Megaphone, Loader2, QrCode, FileText, LogOut, Check, X, Hash, Share2 } from "lucide-react";
import { QRCodeSVG } from "qrcode.react";

const FLYER_BASE = "https://flyer.indiqr.com.br/c";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { useCampaigns, useAcceptCampaign, useDeclineCampaign, useCloseCampaign } from "@/hooks/useCampaigns";
import { DataPagination } from "@/components/shared/DataPagination";
import { toast } from "sonner";
import api from "@/lib/axios";
import type { Campaign } from "@/types/api";

async function downloadBlob(url: string, filename: string) {
  const response = await api.get(url, { responseType: "blob" });
  const objectUrl = URL.createObjectURL(response.data as Blob);
  const a = document.createElement("a");
  a.href = objectUrl;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(objectUrl);
}

function CampaignCard({ campaign }: { campaign: Campaign }) {
  const [downloading, setDownloading] = useState<"qr" | "pdf" | null>(null);
  const [sharing, setSharing] = useState(false);
  const accept = useAcceptCampaign();
  const decline = useDeclineCampaign();
  const leave = useCloseCampaign();

  async function handleDownload(type: "qr" | "pdf") {
    setDownloading(type);
    try {
      if (type === "qr") {
        await downloadBlob(`/campaigns/${campaign.id}/qrcode`, `qrcode-${campaign.id}.png`);
      } else {
        await downloadBlob(`/campaigns/${campaign.id}/card.pdf`, `card-${campaign.id}.pdf`);
      }
    } catch {
      toast.error("Erro ao baixar arquivo.");
    } finally {
      setDownloading(null);
    }
  }

  function handleAccept() {
    accept.mutate(campaign.id, {
      onSuccess: () => toast.success("Campanha aceita!"),
      onError: (err: unknown) => {
        const msg = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail ?? "Erro ao aceitar campanha";
        toast.error(msg);
      },
    });
  }

  function handleDecline() {
    if (!confirm("Recusar esta campanha?")) return;
    decline.mutate(campaign.id, {
      onSuccess: () => toast.success("Campanha recusada."),
      onError: (err: unknown) => {
        const msg = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail ?? "Erro ao recusar campanha";
        toast.error(msg);
      },
    });
  }

  function handleLeave() {
    if (!confirm("Sair desta campanha? Esta ação não pode ser desfeita.")) return;
    leave.mutate(campaign.id, {
      onSuccess: () => toast.success("Você saiu da campanha."),
      onError: (err: unknown) => {
        const msg = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail ?? "Erro ao sair da campanha";
        toast.error(msg);
      },
    });
  }

  async function handleShare() {
    if (!campaign.short_token) return;
    const flyerUrl = `${FLYER_BASE}/${campaign.short_token}`;
    const imageUrl = `${flyerUrl}/image.png`;

    setSharing(true);
    try {
      if (navigator.share) {
        const shareData: ShareData = {
          title: campaign.name,
          text: `${campaign.name} — use meu código de indicação`,
          url: flyerUrl,
        };

        if (navigator.canShare) {
          try {
            const blob = await fetch(imageUrl).then((r) => r.blob());
            const file = new File([blob], `flyer-${campaign.short_token}.png`, { type: "image/png" });
            if (navigator.canShare({ files: [file] })) {
              await navigator.share({ ...shareData, files: [file] });
              return;
            }
          } catch {
            // segue sem arquivo
          }
        }

        await navigator.share(shareData);
      } else {
        await navigator.clipboard.writeText(flyerUrl);
        toast.success("Link do flyer copiado!");
      }
    } catch (err) {
      if ((err as Error).name !== "AbortError") {
        toast.error("Erro ao compartilhar.");
      }
    } finally {
      setSharing(false);
    }
  }

  const remuneracao =
    campaign.remuneracao_modelo === "percentual"
      ? `${campaign.remuneracao_valor}% por venda`
      : `R$ ${campaign.remuneracao_valor} por venda`;

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-2">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-primary/10 rounded-lg text-primary shrink-0">
              <Megaphone className="h-4 w-4" />
            </div>
            <CardTitle className="text-base">{campaign.name}</CardTitle>
          </div>
          {campaign.desconto_pct && (
            <Badge variant="outline" className="shrink-0 text-xs">
              {campaign.desconto_pct}% desconto
            </Badge>
          )}
        </div>
        <p className="text-sm text-muted-foreground pl-11">{remuneracao}</p>
      </CardHeader>

      {campaign.status === "aguardando_aceite" && (
        <CardFooter className="gap-2 pt-0">
          <Button
            variant="outline"
            size="sm"
            className="gap-1.5 text-destructive hover:text-destructive"
            onClick={handleDecline}
            disabled={decline.isPending || accept.isPending}
          >
            <X className="h-3.5 w-3.5" />
            Recusar
          </Button>
          <Button
            size="sm"
            className="gap-1.5"
            onClick={handleAccept}
            disabled={accept.isPending || decline.isPending}
          >
            {accept.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />}
            Aceitar
          </Button>
        </CardFooter>
      )}

      {campaign.status === "ativa" && campaign.short_token && (
        <CardContent className="pt-0 pb-2 space-y-3">
          <div className="flex items-center gap-2 rounded-md border border-dashed bg-muted/50 px-3 py-2">
            <Hash className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
            <span className="text-xs text-muted-foreground">Código para vendedores:</span>
            <span className="font-mono font-bold tracking-widest text-sm">{campaign.short_token}</span>
          </div>
          <QRCodeSVG
            value={campaign.short_token}
            size={128}
            className="mx-auto rounded"
          />
        </CardContent>
      )}

      {campaign.status === "ativa" && (
        <CardFooter className="gap-2 pt-0 flex-wrap">
          <Button
            size="sm"
            className="gap-1.5"
            onClick={handleShare}
            disabled={sharing || downloading !== null}
          >
            {sharing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Share2 className="h-3.5 w-3.5" />}
            Compartilhar
          </Button>
          <Button
            variant="outline"
            size="sm"
            className="gap-1.5"
            onClick={() => handleDownload("qr")}
            disabled={downloading !== null || sharing}
          >
            {downloading === "qr" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <QrCode className="h-3.5 w-3.5" />}
            QR Code
          </Button>
          <Button
            variant="outline"
            size="sm"
            className="gap-1.5"
            onClick={() => handleDownload("pdf")}
            disabled={downloading !== null || sharing}
          >
            {downloading === "pdf" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <FileText className="h-3.5 w-3.5" />}
            Card PDF
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className="gap-1.5 text-destructive hover:text-destructive ml-auto"
            onClick={handleLeave}
            disabled={leave.isPending}
          >
            <LogOut className="h-3.5 w-3.5" />
            Sair
          </Button>
        </CardFooter>
      )}
    </Card>
  );
}

function EmptyState({ label }: { label: string }) {
  return (
    <div className="flex flex-col items-center justify-center rounded-lg border border-dashed py-16 text-center">
      <Megaphone className="h-10 w-10 text-muted-foreground/40 mb-3" />
      <p className="text-muted-foreground text-sm">{label}</p>
    </div>
  );
}

export default function MyCampaigns() {
  const [page, setPage] = useState(1);
  const { data, isLoading } = useCampaigns(page);

  const campaigns = data?.items ?? [];
  const byStatus = (status: string) => campaigns.filter((c) => c.status === status);
  const aguardando = byStatus("aguardando_aceite");
  const ativas = byStatus("ativa");
  const encerradas = byStatus("encerrada");

  const defaultTab = aguardando.length > 0 ? "aguardando" : ativas.length > 0 ? "ativas" : "encerradas";

  if (isLoading) {
    return (
      <div className="flex h-[50vh] items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold text-foreground">Minhas Campanhas</h1>

      <Tabs defaultValue={defaultTab}>
        <TabsList>
          <TabsTrigger value="aguardando" className="gap-1.5">
            Aguardando
            {aguardando.length > 0 && <Badge variant="secondary" className="text-xs px-1.5 py-0">{aguardando.length}</Badge>}
          </TabsTrigger>
          <TabsTrigger value="ativas" className="gap-1.5">
            Ativas
            {ativas.length > 0 && <Badge variant="secondary" className="text-xs px-1.5 py-0">{ativas.length}</Badge>}
          </TabsTrigger>
          <TabsTrigger value="encerradas">Encerradas</TabsTrigger>
        </TabsList>

        <TabsContent value="aguardando" className="space-y-3 mt-4">
          {aguardando.length === 0
            ? <EmptyState label="Nenhum convite de campanha pendente." />
            : aguardando.map((c) => <CampaignCard key={c.id} campaign={c} />)}
        </TabsContent>

        <TabsContent value="ativas" className="space-y-3 mt-4">
          {ativas.length === 0
            ? <EmptyState label="Nenhuma campanha ativa no momento." />
            : ativas.map((c) => <CampaignCard key={c.id} campaign={c} />)}
        </TabsContent>

        <TabsContent value="encerradas" className="space-y-3 mt-4">
          {encerradas.length === 0
            ? <EmptyState label="Nenhuma campanha encerrada." />
            : encerradas.map((c) => <CampaignCard key={c.id} campaign={c} />)}
        </TabsContent>
      </Tabs>

      <DataPagination
        page={data?.page ?? 1}
        pages={data?.pages ?? 1}
        onPageChange={setPage}
      />
    </div>
  );
}
